﻿import { useState, useEffect, useMemo, type ChangeEvent } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectArtifactHeaderConsolidatedInfo,
  selectArtifactDetailConsolidatedInfo,
  updateFields,
} from "@/features/mapSchema/mapSchemaSlice";
import { Typography, Container, Box } from "@mui/material";
import {
  ShepDataGridPro,
  ShepCheckbox,
  ShepPagination,
  ShepSearchBar,
} from "@/components/GenericComponents/ShepComponents";
import type { IMapSchemaField } from "@/features/sharedTypes";

interface IAddEditInboundMappingGrid {
  mappingArea: number;
  selectedRows: string[];
  isEditMode: boolean;
  rowSelectionModel: string[];
  setRowSelectionModel: (data: string[]) => void;
}

const AddEditInboundMappingGrid = ({
  mappingArea,
  selectedRows,
  isEditMode,
  rowSelectionModel,
  setRowSelectionModel,
}: IAddEditInboundMappingGrid) => {
  const dispatch = useDispatch();

  const [searchText, setSearchText] = useState("");

  const artifactHeaderConsolidatedInfo: IMapSchemaField[] = useSelector(
    selectArtifactHeaderConsolidatedInfo,
  );

  const artifactDetailConsolidatedInfo: IMapSchemaField[] = useSelector(
    selectArtifactDetailConsolidatedInfo,
  );

  const gridHeader = mappingArea === 0 ? "Document Header" : "Document Details";
  const columns = [
    {
      field: "field",
      headerName: "Field Name",
      width: 450,
    },
    {
      field: "type",
      headerName: "Type",
      width: 100,
    },
    {
      field: "maxLength",
      headerName: "Length",
      width: 100,
    },
    {
      field: "isOptional",
      headerName: "Is Optional",
      width: 130,
    },
  ];

  const rows =
    mappingArea === 0
      ? artifactHeaderConsolidatedInfo
      : artifactDetailConsolidatedInfo;

  // Find filtered rows dynamically
  const filteredRows = useMemo(() => {
    // Return all rows if the search bar is cleared
    if (!searchText) {
      return rows;
    }

    const lowercasedFilter = searchText.toLowerCase();

    // Filter only by the "Field Name" column
    return rows.filter((row) => {
      const fieldNameValue = row.field ? String(row.field).toLowerCase() : "";
      return fieldNameValue.includes(lowercasedFilter);
    });
  }, [rows, searchText]);

  // Handle user input
  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  useEffect(() => {
    if (
      selectedRows &&
      selectedRows.length > 0 &&
      rowSelectionModel.length === 0
    ) {
      setRowSelectionModel([selectedRows[0]]);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows]); // Update only when selectedRows changes

  const handleRowSelection = (newRowSelectionModel: string[]) => {
    //For Edit
    if (newRowSelectionModel.length > 0 && isEditMode) { // selecting a row initially only results in length of 1, 
      newRowSelectionModel = [newRowSelectionModel.pop() as string];
      setRowSelectionModel(newRowSelectionModel);
    }
    //For Add
    if (!isEditMode) {
      if (newRowSelectionModel.length > 0) {
        //to support single row selection, since the combination of disableMultipleRowSelection={true}
        // and checkboxSelection={true} does not work since checkboxSelection={true} overrides the "disableMultipleRowSelection"
        newRowSelectionModel = [newRowSelectionModel.pop() as string];
        setRowSelectionModel(newRowSelectionModel);
      } else if (rowSelectionModel.length > 1) {
        setRowSelectionModel(newRowSelectionModel);
      } else {
        return;
      }
    }

    const selectedData = rows.find((row) => row.id === newRowSelectionModel[0]);
    if (selectedData != null) {
      dispatch(updateFields({ key: "targetField", value: selectedData.field }));
      dispatch(
        updateFields({
          key: "targetFieldIsOptional",
          value: selectedData.isOptional,
        }),
      );
    }

    if (
      newRowSelectionModel.length === 0
      || newRowSelectionModel[0] !== rowSelectionModel[0]
    ) {
      return; // No change in selection, so exit early
    }
  };

  return (
    <Container>
      <Box sx={{ width: "100%", maxWidth: "100%", overflowX: "auto" }}>
        <Box sx={{ marginTop: "50px", marginBottom: "20px" }}>
          <ShepSearchBar
            placeholder="Search Field Name"
            value={searchText}
            onChange={handleSearchChange}
            style={{ width: "500px" }}
          />
        </Box>
        <Box
          sx={{
            height: "78px",
            backgroundColor: "var(--primary-main)",
            color: "var(--default-light)",
            padding: "20px 0px 20px 53px",
            borderRadius: "4px",
            border: "1px black",
            boxSizing: "border-box",
          }}
        >
          <Typography
            style={{
              fontSize: "var(--font-lg)",
              fontWeight: "var(--font-semi-bold)",
            }}
          >
            {gridHeader}
          </Typography>
        </Box>
        {filteredRows.length > 0 ? (
          <ShepDataGridPro
            isDataLoaded={true}
            rows={filteredRows}
            columns={columns}
            getRowId={(row: IMapSchemaField) => row.id}
            columnHeaderHeight={80}
            rowHeight={80}
            pagination
            initialState={{
              pagination: {
                paginationModel: { pageSize: 5, page: 0 },
              },
            }}
            whiteHeader={true}
            checkboxSelection={true}
            disableMultipleRowSelection={false}
            disableColumnMenu={true}
            disableColumnFilter={true}
            disableColumnReorder={true}
            hideFooterSelectedRowCount={true}
            columnVisibilityModel={{
              endpointSchemaId: false,
            }}
            //rowSelectionModel={selectedRows}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={handleRowSelection}
            disableRowSelectionOnClick={true}
            slots={{
              baseCheckbox: ShepCheckbox,
              pagination: ShepPagination,
            }}
            sx={{
              color: "var(--text-primary)",
              fontSize: "var(--font-sm)",
              fontFamily: "var(--font-family)",
              borderTopLeftRadius: "44px",
              borderTopRightRadius: "44px",
              backgroundColor: "var(--default-light)",
              border: "none",
              "& .MuiDataGrid:hover": {
                backgroundColor: "inherit",
              },
              "& .MuiDataGrid-cellCheckbox, & .workflow-row": {
                opacity: 0,
                borderBottom: "none",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "none",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "var(--tertiary-13)",
              },
              "& .Mui-selected": {
                backgroundColor: "var(--primary-pale)",
              },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus":
                {
                  outline: "none",
                },
              "& .MuiDataGrid-row:hover .MuiDataGrid-cellCheckbox, & .MuiDataGrid-row:hover .workflow-row":
                {
                  opacity: 1,
                },
              "& .Mui-selected .MuiDataGrid-cellCheckbox": {
                opacity: 1,
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontSize: "var(--font-md)",
                fontWeight: "var(--font-bold)",
                borderLeft: "none",
              },
              "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader:hover":
                {
                  borderBottomLeftRadius: "4px",
                  borderBottomRightRadius: "4px",
                  backgroundColor: "var(--primary-pale)",
                  borderBottom: "none",
                  borderLeft: "none",
                },
              "& .MuiDataGrid-columnHeader[data-field='displayName']": {
                marginLeft: 0,
              },
              "& .MuiDataGrid-cell[data-field='displayName']": {
                marginLeft: 0,
              },
            }}
          />
        ) : (
          <Typography variant="h6" sx={{ textAlign: "center", mt: 2 }}>
            No data available
          </Typography>
        )}
      </Box>
    </Container>
  );
};
export default AddEditInboundMappingGrid;
